/**
 * Admiralty UK Tidal API client — calls go through the same-origin dev proxy
 * (/admiralty/...), which the Vite dev server forwards to the real API while
 * attaching the subscription key SERVER-SIDE (from ADMIRALTY_KEY in .env.local).
 * That fixes the browser CORS block and keeps the key off the client. A key
 * entered in Settings is sent as a header and overrides the proxy's env key.
 *
 * Free "Discovery" tier = tidal HEIGHT events (HW/LW) for ~600 UK stations; the
 * paid "Foundation" tier adds STREAM events. Get a free key at the ADMIRALTY
 * Developer Portal (developer.admiralty.co.uk), subscribe to UK Tidal API
 * Discovery, and copy the Primary key.
 */
import { Capacitor } from '@capacitor/core';

const BASE = '/admiralty/uktidalapi/api/V1';

/**
 * Is the Admiralty source usable? True if the user entered a Settings key, OR the
 * dev proxy has a key configured (VITE_ADMIRALTY_PROXY — a non-secret flag; the
 * key itself stays server-side and out of the bundle).
 *
 * The packaged/native app has NO dev proxy, and BASE is a relative same-origin
 * path (/admiralty/...) that only resolves behind that proxy — there's no direct
 * absolute endpoint to hit from the WebView. So on native Admiralty can't be
 * reached at all: we report it unavailable rather than "configured" and then
 * failing every fetch against the app's own origin (the exact trap the old
 * online-AIS bug fell into). Native is checked FIRST because VITE_ADMIRALTY_PROXY
 * is baked into the native bundle too and would otherwise read as configured.
 * `native` is injectable so the branch is easy to unit-test.
 */
/** The baked build flag, parsed once. Injectable in admiraltyConfigured so the
 *  tests are about the LOGIC, not about which .env the machine running them
 *  happens to have. The old tests read the real flag, which made a clean
 *  checkout red and Edd's machine green: the worst kind of test, one that
 *  reports on its environment instead of its subject. */
export function admiraltyProxyFlag(): boolean {
  const flag = (import.meta.env as Record<string, string | undefined>).VITE_ADMIRALTY_PROXY ?? '';
  return flag === '1' || flag.toLowerCase() === 'true';
}

export function admiraltyConfigured(
  settingsKey: string,
  native: boolean = Capacitor.isNativePlatform(),
  proxyFlag: boolean = admiraltyProxyFlag(),
): boolean {
  if (native) return false;
  if (settingsKey.trim()) return true;
  return proxyFlag;
}

export interface AdmiraltyEvent {
  /** 'HighWater' | 'LowWater'. */
  EventType: string;
  /** ISO datetime (UTC). */
  DateTime: string;
  /** Height, metres. */
  Height: number;
}

interface FetchLike {
  (url: string, init?: { headers?: Record<string, string> }): Promise<{
    ok: boolean;
    status: number;
    json: () => Promise<unknown>;
  }>;
}

/** Only send the key header when the user supplied a Settings override; otherwise
 *  the proxy attaches the env key, so nothing secret leaves the client. */
function authHeaders(key?: string): Record<string, string> {
  const k = (key ?? '').trim();
  return k ? { 'Ocp-Apim-Subscription-Key': k } : {};
}

/** Tidal HW/LW events for a station id. Throws on failure. */
export async function fetchAdmiraltyEvents(
  stationId: string,
  key?: string,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<AdmiraltyEvent[]> {
  const r = await fetchImpl(`${BASE}/Stations/${encodeURIComponent(stationId)}/TidalEvents`, {
    headers: authHeaders(key),
  });
  if (!r.ok) throw new Error(`Admiralty API ${r.status}`);
  const data = (await r.json()) as AdmiraltyEvent[] | { error?: string };
  return Array.isArray(data) ? data : [];
}

/** The nearest Admiralty station to a position, from the station list. */
export interface AdmiraltyStation {
  id: string;
  name: string;
  position: [number, number];
  /** Country that provided the station data (free-tier metadata). */
  country?: string;
  /** Whether continuous heights are published — the nearest free-tier signal of a
   *  Standard port (with a published curve) vs a Secondary port. */
  continuousHeights?: boolean;
}

export async function fetchAdmiraltyStations(
  key?: string,
  fetchImpl: FetchLike = fetch as unknown as FetchLike,
): Promise<AdmiraltyStation[]> {
  const r = await fetchImpl(`${BASE}/Stations`, { headers: authHeaders(key) });
  if (!r.ok) throw new Error(`Admiralty API ${r.status}`);
  const data = (await r.json()) as {
    features?: Array<{
      properties?: { Id?: string; Name?: string; Country?: string; ContinuousHeightsAvailable?: boolean };
      geometry?: { coordinates?: [number, number] };
    }>;
  };
  return (data.features ?? [])
    .map((f) => ({
      id: String(f.properties?.Id ?? ''),
      name: String(f.properties?.Name ?? ''),
      position: (f.geometry?.coordinates ?? [0, 0]) as [number, number],
      country: f.properties?.Country,
      continuousHeights: f.properties?.ContinuousHeightsAvailable,
    }))
    .filter((s) => s.id);
}
